import { ClipboardEvent, FormEvent, KeyboardEvent, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LinfinityLogo } from "@/assets/logos";
import type { IfcViewerHandle } from "@/components/ifc/IfcViewer";
import IfcPropertyPanel, { type IfcCategoryFilter, type IfcSelectionProperties } from "@/components/ifc/IfcPropertyPanel";
import IfcViewerError from "@/components/ifc/IfcViewerError";
import IfcViewerToolbar from "@/components/ifc/IfcViewerToolbar";
import "./IfcViewerPage.css";

const IfcViewer = lazy(() => import("@/components/ifc/IfcViewer"));
const IFC_PUBLIC_VIEWER_BASE_PATH = "/projects/ifc";
const IFC_VIEWER_DISCLAIMER =
  "3D views of elements are indicative only. Refer to the project architectural and engineering drawings. Contact us for clarification.";

type IfcBootstrap = {
  fileName: string;
  fileSizeBytes: number | null;
  viewerUrl: string;
  expiresAt: string;
  versionLabel: string | null;
  versionCreatedAt: string | null;
};

type IfcMatch = {
  ifcFileId: string;
  fileName: string;
  fileSizeBytes: number | null;
  projectNumber: string | null;
  versionLabel?: string | null;
  versionCreatedAt?: string | null;
};

type BootstrapState =
  | { status: "loading" }
  | { status: "ready"; data: IfcBootstrap }
  | { status: "error"; code?: string; message: string };

async function readJson(response: Response) {
  return response.json().catch(() => null);
}

function publicErrorMessage(payload: any, fallback: string): string {
  return payload?.error?.message || fallback;
}

function IfcViewerDisclaimer({ placement }: { placement: "entry" | "viewer" }) {
  return (
    <p className={`ifc-viewer-disclaimer ifc-viewer-disclaimer--${placement}`}>
      {IFC_VIEWER_DISCLAIMER}
    </p>
  );
}

function OtpCodeInput({ value, onChange, disabled = false }: { value: string; onChange: (value: string) => void; disabled?: boolean }) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: 6 }, (_, index) => value[index] || "");

  const setDigitAt = (index: number, nextValue: string) => {
    const numeric = nextValue.replace(/\D/g, "");
    if (numeric.length > 1) {
      const nextCode = numeric.slice(0, 6);
      onChange(nextCode);
      inputsRef.current[Math.min(nextCode.length, 5)]?.focus();
      return;
    }

    const nextDigits = [...digits];
    nextDigits[index] = numeric;
    onChange(nextDigits.join("").slice(0, 6));
    if (numeric && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      const nextDigits = [...digits];
      nextDigits[index - 1] = "";
      onChange(nextDigits.join(""));
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    inputsRef.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <fieldset className="ifc-otp-code" aria-label="Email code">
      <legend>Email code</legend>
      <div className="ifc-otp-code__boxes">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputsRef.current[index] = element;
            }}
            aria-label={`Email code digit ${index + 1}`}
            value={digit}
            onChange={(event) => setDigitAt(index, event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onPaste={handlePaste}
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            pattern="[0-9]*"
            maxLength={1}
            disabled={disabled}
          />
        ))}
      </div>
    </fieldset>
  );
}

function PublicIfcEntry() {
  const navigate = useNavigate();
  const [projectNumber, setProjectNumber] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [matches, setMatches] = useState<IfcMatch[]>([]);
  const [selectedIfcFileId, setSelectedIfcFileId] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [status, setStatus] = useState<"idle" | "searching" | "sending" | "verifying">("idle");
  const [message, setMessage] = useState<string | null>(null);

  const selectedMatch = matches.find((match) => match.ifcFileId === selectedIfcFileId) || null;

  const search = async (event: FormEvent) => {
    event.preventDefault();
    setStatus("searching");
    setMessage(null);
    setMatches([]);
    setSelectedIfcFileId("");
    setVerificationId("");

    const response = await fetch("/api/public/ifc-viewer/search", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ projectNumber, streetAddress }),
    });
    const payload = await readJson(response);
    setStatus("idle");

    if (!response.ok) {
      setMessage(publicErrorMessage(payload, "No IFC model matched those details."));
      return;
    }

    const nextMatches = Array.isArray(payload?.matches) ? payload.matches : [];
    setMatches(nextMatches);
    setSelectedIfcFileId(nextMatches[0]?.ifcFileId || "");
    setMessage(nextMatches.length === 1 ? "One IFC model matched. Request an email code to continue." : "Select the IFC model to open.");
  };

  const requestOtp = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedIfcFileId) {
      setMessage("Search for a matching IFC model first.");
      return;
    }

    setStatus("sending");
    setMessage(null);
    const response = await fetch("/api/public/ifc-viewer/otp/request", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ifcFileId: selectedIfcFileId, requesterName, requesterEmail }),
    });
    const payload = await readJson(response);
    setStatus("idle");

    if (!response.ok) {
      setMessage(publicErrorMessage(payload, "The email code could not be sent."));
      return;
    }

    setVerificationId(payload?.verificationId || "");
    setMessage("Email code sent. Enter the 6 digit code to open the viewer session.");
  };

  const verifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    setStatus("verifying");
    setMessage(null);
    const response = await fetch("/api/public/ifc-viewer/otp/verify", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ verificationId, requesterEmail, otpCode }),
    });
    const payload = await readJson(response);
    setStatus("idle");

    if (!response.ok) {
      setMessage(publicErrorMessage(payload, "The email code could not be verified."));
      return;
    }

    navigate(payload?.viewerPath || `${IFC_PUBLIC_VIEWER_BASE_PATH}/${encodeURIComponent(payload?.sessionToken || "")}`, { replace: true });
  };

  return (
    <main className="ifc-public-page ifc-public-page--center">
      <section className="ifc-entry-panel" aria-label="Open public IFC viewer">
        <div className="ifc-entry-panel__brand">
          <div className="ifc-entry-panel__logo">
            <LinfinityLogo size="horizontal" width={212} height={68} />
          </div>
          <span>Engineering project viewer</span>
        </div>

        <div className="ifc-entry-panel__intro">
          <div className="ifc-entry-panel__header">
            <span>Linfinity Engineering</span>
            <h1>Open a Linfinity Engineering project model</h1>
            <p>Secure BIM access for structural, civil and geotechnical engineering projects prepared by Linfinity Engineering.</p>
          </div>
          <div className="ifc-entry-panel__project-mark" aria-hidden="true">
            <span>IFC</span>
            <strong>Model</strong>
          </div>
        </div>

        <IfcViewerDisclaimer placement="entry" />

        <form className="ifc-entry-form" onSubmit={search}>
          <label>
            Project number
            <input value={projectNumber} onChange={(event) => setProjectNumber(event.target.value)} autoComplete="off" />
          </label>
          <label>
            Project street address
            <input value={streetAddress} onChange={(event) => setStreetAddress(event.target.value)} autoComplete="off" />
          </label>
          <button type="submit" disabled={status !== "idle"}>{status === "searching" ? "Searching..." : "Search"}</button>
        </form>

        {matches.length > 0 && (
          <form className="ifc-entry-form" onSubmit={requestOtp}>
            <label>
              IFC model
              <select value={selectedIfcFileId} onChange={(event) => setSelectedIfcFileId(event.target.value)}>
                {matches.map((match) => (
                  <option key={match.ifcFileId} value={match.ifcFileId}>
                    {match.fileName}{match.versionLabel ? ` (${match.versionLabel})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Name
              <input value={requesterName} onChange={(event) => setRequesterName(event.target.value)} autoComplete="name" />
            </label>
            <label>
              Email
              <input value={requesterEmail} onChange={(event) => setRequesterEmail(event.target.value)} autoComplete="email" inputMode="email" />
            </label>
            <button type="submit" disabled={status !== "idle"}>{status === "sending" ? "Sending..." : "Send email code"}</button>
          </form>
        )}

        {verificationId && (
          <form className="ifc-entry-form" onSubmit={verifyOtp}>
            <OtpCodeInput value={otpCode} onChange={setOtpCode} disabled={status !== "idle"} />
            <button type="submit" disabled={status !== "idle" || otpCode.length !== 6}>{status === "verifying" ? "Verifying..." : "Open viewer"}</button>
          </form>
        )}

        {selectedMatch && (
          <p className="ifc-entry-panel__hint">
            Selected: {selectedMatch.fileName}{selectedMatch.versionLabel ? ` · Version ${selectedMatch.versionLabel}` : ""}
          </p>
        )}
        {message && <p className="ifc-entry-panel__message">{message}</p>}
      </section>
    </main>
  );
}

function IfcSessionViewer({ token }: { token: string }) {
  const viewerRef = useRef<IfcViewerHandle | null>(null);
  const [bootstrap, setBootstrap] = useState<BootstrapState>({ status: "loading" });
  const [selection, setSelection] = useState<IfcSelectionProperties | null>(null);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [categoryFilters, setCategoryFilters] = useState<IfcCategoryFilter[]>([]);
  const [categoryBusy, setCategoryBusy] = useState<string | null>(null);
  const [categoryStatus, setCategoryStatus] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadBootstrap = async () => {
      try {
        setBootstrap({ status: "loading" });
        const response = await fetch(`/api/public/ifc-viewer/session/${encodeURIComponent(token)}`, { cache: "no-store" });
        const payload = await readJson(response);
        if (cancelled) return;

        if (!response.ok) {
          setBootstrap({
            status: "error",
            code: payload?.error?.code,
            message: payload?.error?.message || "This IFC viewer session could not be opened.",
          });
          return;
        }

        setBootstrap({ status: "ready", data: payload as IfcBootstrap });
      } catch {
        if (!cancelled) {
          setBootstrap({ status: "error", message: "The IFC viewer could not reach the server." });
        }
      }
    };

    loadBootstrap();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const resetView = useCallback(() => {
    viewerRef.current?.resetView();
  }, []);

  const setCategoryVisible = useCallback((category: string, visible: boolean) => {
    viewerRef.current?.setCategoryVisible(category, visible);
  }, []);

  const setCategoryAppearance = useCallback((category: string, appearance: Partial<Pick<IfcCategoryFilter, "color" | "opacity">>) => {
    viewerRef.current?.setCategoryAppearance(category, appearance);
  }, []);

  const showAllCategories = useCallback(() => {
    viewerRef.current?.showAllCategories();
  }, []);

  if (bootstrap.status === "loading") {
    return (
      <main className="ifc-public-page ifc-public-page--center">
        <div className="ifc-loading-panel">
          <strong>Opening IFC model...</strong>
          <span>Preparing a short-lived viewer URL.</span>
        </div>
      </main>
    );
  }

  if (bootstrap.status === "error") {
    return (
      <main className="ifc-public-page ifc-public-page--center">
        <IfcViewerError title={bootstrap.code || "Viewer session unavailable"} message={bootstrap.message} />
      </main>
    );
  }

  return (
    <main className="ifc-public-page">
      <IfcViewerToolbar
        fileName={bootstrap.data.fileName}
        versionLabel={bootstrap.data.versionLabel}
        expiresAt={bootstrap.data.expiresAt}
        onResetView={resetView}
      />
      <IfcViewerDisclaimer placement="viewer" />
      <section className="ifc-public-page__body">
        <Suspense fallback={<div className="ifc-viewer ifc-viewer--loading">Loading viewer engine...</div>}>
          <IfcViewer
            ref={viewerRef}
            fileUrl={bootstrap.data.viewerUrl}
            fileName={bootstrap.data.fileName}
            onSelectionChange={setSelection}
            onSelectionLoadingChange={setSelectionLoading}
            onCategoryFiltersChange={setCategoryFilters}
            onCategoryBusyChange={setCategoryBusy}
            onCategoryStatusChange={setCategoryStatus}
          />
        </Suspense>
        <IfcPropertyPanel
          selection={selection}
          loading={selectionLoading}
          categoryFilters={categoryFilters}
          categoryBusy={categoryBusy}
          categoryStatus={categoryStatus}
          onCategoryVisibleChange={setCategoryVisible}
          onCategoryAppearanceChange={setCategoryAppearance}
          onShowAllCategories={showAllCategories}
        />
      </section>
    </main>
  );
}

export default function IfcViewerPage() {
  const { token } = useParams();
  return token ? <IfcSessionViewer token={token} /> : <PublicIfcEntry />;
}
